import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "flowbite-react";
import { AiOutlineLoading } from "react-icons/ai";
import { NavBar, SideNav } from "../../components";
import Breadcrumb from "../../components/Breadcrumb";
import { fetchCompleteness } from "../../redux/slices/aiCompleteness";
import { programmeHref } from "../../helpers/programmeHref";
import {
  COUNTRY_OPTIONS,
  countryLabel,
} from "../../helpers/advisorVocab";

const TYPES = [
  { value: "programme", label: "Programme" },
  { value: "scholarship", label: "Scholarship" },
  { value: "school", label: "School" },
];

const PAGE_LIMIT = 25;

function editorLink(row, type) {
  if (type === "school") {
    return `/school/${row.id}`;
  }
  if (type === "scholarship") {
    return `/scholarship/${row.id}`;
  }
  if (type === "programme") {
    return programmeHref({
      schoolId: row.articleId,
      programmeId: row.id,
      isDraft: false,
    });
  }
  return null;
}

const AiCompleteness = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    data,
    schools,
    lastDocId,
    hasMore,
    pageLoading,
    error,
    type: storeType,
  } = useSelector((state) => state.aiCompleteness);

  const type = searchParams.get("type") || "programme";
  const schoolId = searchParams.get("school") || "";
  const country = (searchParams.get("country") || "").toUpperCase();
  const maxScore = Number(searchParams.get("max") || 1);

  const [maxText, setMaxText] = useState(String(maxScore));

  useEffect(() => {
    setMaxText(String(maxScore));
  }, [maxScore]);

  useEffect(() => {
    dispatch(
      fetchCompleteness({
        type,
        max: maxScore,
        limit: PAGE_LIMIT,
        schoolId,
        country,
      })
    );
  }, [dispatch, type, maxScore, schoolId, country]);

  const patchParams = (patch) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (value == null || value === "") params.delete(key);
      else params.set(key, String(value));
    });
    setSearchParams(params);
  };

  const loadMore = () => {
    if (!lastDocId) return;
    dispatch(
      fetchCompleteness({
        type,
        max: maxScore,
        limit: PAGE_LIMIT,
        lastDocId,
        schoolId,
        country,
      })
    );
  };

  const clearFilters = () => {
    setMaxText("1");
    setSearchParams(new URLSearchParams());
  };

  const schoolOptions = useMemo(() => {
    if (!country) return schools || [];
    return (schools || []).filter(
      (s) => String(s.country || "").toUpperCase() === country
    );
  }, [schools, country]);

  const activeType = type || storeType || "programme";
  const showUniversity =
    activeType === "programme" || activeType === "school";
  const hasFilters = Boolean(schoolId || country || maxScore < 1 || type !== "programme");

  return (
    <div className="antialiased bg-gray-50 dark:bg-gray-900 min-h-screen">
      <NavBar />
      <SideNav />
      <main className="p-4 md:ml-64 h-auto pt-20">
        <div className="sticky top-14 z-10 bg-gray-50 dark:bg-gray-900">
          <Breadcrumb />
        </div>

        <section className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
              AI Data Completeness
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Records with low completeness for matching. Filter by university
              or country, then open a row to fill missing fields.
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <label className="text-sm text-gray-600">
              Type
              <select
                className="mt-1 block rounded-lg border-gray-300 text-sm"
                value={activeType}
                onChange={(e) =>
                  patchParams({
                    type: e.target.value === "programme" ? "" : e.target.value,
                    // School filter only applies to programmes / schools.
                    school:
                      e.target.value === "scholarship" ? "" : schoolId || "",
                  })
                }
              >
                {TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </label>
            {showUniversity && (
              <label className="text-sm text-gray-600">
                University
                <select
                  className="mt-1 block rounded-lg border-gray-300 text-sm max-w-xs"
                  value={schoolId}
                  onChange={(e) => patchParams({ school: e.target.value })}
                >
                  <option value="">All universities</option>
                  {schoolOptions.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.title}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label className="text-sm text-gray-600">
              Country
              <select
                className="mt-1 block rounded-lg border-gray-300 text-sm max-w-[12rem]"
                value={country}
                onChange={(e) => patchParams({ country: e.target.value })}
              >
                <option value="">All countries</option>
                {COUNTRY_OPTIONS.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm text-gray-600">
              Max score
              <input
                type="number"
                min={0}
                max={1}
                step={0.1}
                className="mt-1 block w-24 rounded-lg border-gray-300 text-sm"
                value={maxText}
                onChange={(e) => setMaxText(e.target.value)}
                onBlur={() => {
                  const n = Number(maxText);
                  const next = Number.isFinite(n)
                    ? Math.min(Math.max(n, 0), 1)
                    : 1;
                  setMaxText(String(next));
                  patchParams({ max: next >= 1 ? "" : next });
                }}
              />
            </label>
            <Button
              color="purple"
              disabled={pageLoading}
              onClick={() =>
                dispatch(
                  fetchCompleteness({
                    type: activeType,
                    max: maxScore,
                    limit: PAGE_LIMIT,
                    schoolId,
                    country,
                  })
                )
              }
            >
              Refresh
            </Button>
            {hasFilters && (
              <Button color="light" onClick={clearFilters}>
                Clear filters
              </Button>
            )}
          </div>
        </section>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 border border-red-100 text-sm">
            {String(error)}
          </div>
        )}

        <div className="bg-white dark:bg-gray-800 relative shadow-md sm:rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
              <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                <tr>
                  <th className="px-6 py-3">Title</th>
                  {activeType === "programme" && (
                    <th className="px-6 py-3">University</th>
                  )}
                  <th className="px-6 py-3">Score</th>
                  <th className="px-6 py-3">Country</th>
                  <th className="px-6 py-3">Missing</th>
                  <th className="px-6 py-3">Editor</th>
                </tr>
              </thead>
              <tbody>
                {pageLoading && data.length === 0 ? (
                  <tr>
                    <td
                      colSpan={activeType === "programme" ? 6 : 5}
                      className="px-6 py-8 text-center"
                    >
                      <AiOutlineLoading className="inline h-6 w-6 animate-spin text-purple-600" />
                    </td>
                  </tr>
                ) : data.length === 0 ? (
                  <tr>
                    <td
                      colSpan={activeType === "programme" ? 6 : 5}
                      className="px-6 py-8 text-center"
                    >
                      No incomplete records for this filter.
                    </td>
                  </tr>
                ) : (
                  data.map((row, index) => {
                    const href = editorLink(row, activeType);
                    return (
                      <tr
                        key={row.id}
                        className={`border-b dark:border-gray-700 ${
                          index % 2 === 0 ? "bg-white" : "bg-gray-50"
                        } dark:bg-gray-800`}
                      >
                        <td className="px-6 py-4 font-medium text-gray-900 dark:text-white">
                          {row.title || row.id}
                        </td>
                        {activeType === "programme" && (
                          <td className="px-6 py-4">
                            {row.schoolName || "—"}
                          </td>
                        )}
                        <td className="px-6 py-4">
                          {typeof row.completenessScore === "number"
                            ? row.completenessScore.toFixed(2)
                            : "—"}
                        </td>
                        <td className="px-6 py-4">
                          {countryLabel(row.country) || "—"}
                        </td>
                        <td className="px-6 py-4">
                          {(row.missingHints || []).length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {(row.missingHints || []).map((h) => (
                                <span
                                  key={h}
                                  className="text-[11px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-100"
                                >
                                  {h}
                                </span>
                              ))}
                            </div>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {href ? (
                            <Link
                              to={href}
                              className="text-purple-600 hover:underline text-sm"
                            >
                              Open
                            </Link>
                          ) : (
                            <code className="text-xs text-gray-400">
                              {row.id}
                            </code>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {hasMore && (
            <div className="p-4 flex justify-center border-t border-gray-100 dark:border-gray-700">
              <Button
                color="gray"
                disabled={pageLoading}
                isProcessing={pageLoading}
                onClick={loadMore}
              >
                Load more
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AiCompleteness;

